// Placeholder API layer.
//
// Each function here corresponds to an endpoint in Section 5 (REST API) /
// Section 8 (AI/ML backend) of the CanLexa spec. They currently return mock
// data with an artificial delay so the UI can be built and reviewed before
// the real backend exists. Swap the internals for real `fetch` calls once
// Core Services / the AI backend are live — the function signatures below
// are designed to stay the same.

import {
  SUPPORTING_AUTHORITY,
  type Workspace,
  type ExtractedFacts,
  type WorkspaceDocument,
  type SearchResult,
} from "./mockData";



const FAKE_LATENCY_MS = 300;

function delay<T>(value: T): Promise<T> {
  return new Promise((resolve) => setTimeout(() => resolve(value), FAKE_LATENCY_MS));
}

// POST /api/auth/login
export async function login(credentials: { email?: string; password?: string }): Promise<{ ok: boolean; token?: string; error?: string }> {
  const res = await fetch("/api/auth/login", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(credentials),
  });
  const data = await res.json();
  if (!res.ok || data.error) {
    throw new Error(data.error || "Invalid credentials");
  }
  if (data.token && typeof window !== "undefined") {
    localStorage.setItem("canlexa_jwt", data.token);
  }
  return { ok: true, token: data.token };
}

// POST /api/auth/signup
export async function signupUser(params: {
  full_name: string;
  email: string;
  password: string;
  firm_name?: string;
}) {
  const res = await fetch("/api/auth/signup", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(params),
  });
  const data = await res.json();
  if (!res.ok || data.error) {
    throw new Error(data.error || "Failed to sign up");
  }
  return data;
}

// GET /api/auth/me
export async function getCurrentUser() {
  const token = typeof window !== "undefined" ? localStorage.getItem("canlexa_jwt") : null;
  const headers: Record<string, string> = {};
  if (token) headers["Authorization"] = `Bearer ${token}`;

  const res = await fetch("/api/auth/me", { headers });
  const data = await res.json();
  if (!res.ok) return null;
  return data.user;
}

// GET /api/user/profile
export async function getUserProfile() {
  const token = typeof window !== "undefined" ? localStorage.getItem("canlexa_jwt") : null;
  const headers: Record<string, string> = {};
  if (token) headers["Authorization"] = `Bearer ${token}`;

  const res = await fetch("/api/user/profile", { headers });
  const data = await res.json();
  if (!res.ok || data.error) return null;
  return data.user;
}

// PATCH /api/user/profile
export async function updateUserProfile(profileData: {
  full_name?: string;
  firm_name?: string;
  title?: string;
  practice_area?: string;
  bar_number?: string;
  phone_number?: string;
  bio?: string;
}) {
  const token = typeof window !== "undefined" ? localStorage.getItem("canlexa_jwt") : null;
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (token) headers["Authorization"] = `Bearer ${token}`;

  const res = await fetch("/api/user/profile", {
    method: "PATCH",
    headers,
    body: JSON.stringify(profileData),
  });
  const data = await res.json();
  if (!res.ok || data.error) {
    throw new Error(data.error || "Failed to update profile");
  }
  return data.user;
}

// POST /api/auth/logout
export async function logoutUser() {
  if (typeof window !== "undefined") {
    localStorage.removeItem("canlexa_jwt");
    localStorage.clear();
    sessionStorage.clear();
    document.cookie = "canlexa_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
    document.cookie = "canlexa_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; max-age=0;";
  }
  try {
    await fetch("/api/auth/logout", {
      method: "POST",
      cache: "no-store",
    });
  } catch (err) {
    console.error("Failed to call logout endpoint:", err);
  }
}

// GET /workspaces
export async function listWorkspaces(): Promise<Workspace[]> {
  try {
    const token = typeof window !== "undefined" ? localStorage.getItem("canlexa_jwt") : null;
    const headers: Record<string, string> = {};
    if (token) headers["Authorization"] = `Bearer ${token}`;

    const res = await fetch("/api/workspaces", { headers });
    if (!res.ok) return [];
    const data = await res.json();
    if (!Array.isArray(data.workspaces)) return [];
    return data.workspaces.map((w: any) => ({
      id: w.id,
      title: w.title,
      type: w.matter_type || "litigation",
      sub: w.matter_type === "transactional" ? "Transactional File" : "Ontario Civil Litigation",
      docs: 1,
      updated: new Date(w.created_at).toLocaleDateString(),
    }));
  } catch (err) {
    console.error("Error listing workspaces:", err);
    return [];
  }
}


// GET /workspaces/{id}
export async function getWorkspace(id: string): Promise<Workspace | undefined> {
  const workspaces = await listWorkspaces();
  return workspaces.find((w) => w.id === id);
}

// GET /documents/{id}/extraction (or by workspace ID)
export async function getExtraction(id: string): Promise<ExtractedFacts | null> {
  try {
    const res = await fetch(`/api/documents/${id}/extraction`);
    if (!res.ok) {
      if (res.status === 404) return null;
      throw new Error("Failed to fetch extraction");
    }
    const data = await res.json();
    return data.extraction || null;
  } catch (error) {
    console.error("Error fetching extraction:", error);
    return null;
  }
}

// GET /workspaces/{id} (documents list portion)
export async function getWorkspaceDocuments(workspaceId: string): Promise<WorkspaceDocument[]> {
  try {
    const res = await fetch(`/api/workspaces/${workspaceId}/documents`);
    if (!res.ok) return [];
    const data = await res.json();
    return Array.isArray(data.documents) ? data.documents : [];
  } catch (error) {
    console.error("Error fetching workspace documents:", error);
    return [];
  }
}


// POST /api/workspaces/{id}/documents
export async function uploadDocument(
  workspaceId: string,
  file: File
): Promise<{ document: WorkspaceDocument; extraction?: ExtractedFacts; error?: string }> {
  const formData = new FormData();
  formData.append("file", file);

  const res = await fetch(`/api/workspaces/${workspaceId}/documents`, {
    method: "POST",
    body: formData,
  });

  const data = await res.json();
  if (!res.ok || data.error) {
    throw new Error(data.error || "Failed to upload document");
  }
  return data;
}

// DELETE /api/workspaces/{id}/documents?docId={docId}
export async function deleteDocument(workspaceId: string, docId: string): Promise<void> {
  const res = await fetch(`/api/workspaces/${workspaceId}/documents?docId=${docId}`, {
    method: "DELETE",
  });
  const data = await res.json();
  if (!res.ok || data.error) {
    throw new Error(data.error || "Failed to delete document");
  }
}

// DELETE /api/workspaces/{id}/generated-documents/{docId}
export async function deleteGeneratedDocument(workspaceId: string, docId: string): Promise<void> {
  const res = await fetch(`/api/workspaces/${workspaceId}/generated-documents/${docId}`, {
    method: "DELETE",
  });
  const data = await res.json();
  if (!res.ok || data.error) {
    throw new Error(data.error || "Failed to delete generated document");
  }
}

export interface SemanticSearchResultItem {
  judgment_id: string;
  chunk_id: string;
  score: number;
  para_start: number;
  para_end: number;
  text: string;
  neutral_cite: string;
  court: string;
  decided_on: string;
  style_of_cause?: string | null;
  source_url: string;
  full_text?: string | null;
  summary?: string | null;
  summary_status?: "ready" | "pending" | string;
  headnote?: string | null;
}


export interface SemanticSearchResponse {
  query: string;
  total_found?: number;
  total_count?: number;
  total?: number;
  total_results?: number;
  results: SemanticSearchResultItem[];
}

function getFastApiDirectBase() {
  if (typeof window !== "undefined") {
    return `${window.location.protocol}//${window.location.hostname}:8000`;
  }
  if (process.env.NEXT_PUBLIC_API_URL) {
    return process.env.NEXT_PUBLIC_API_URL.replace(/\/$/, "").replace("8888", "8000");
  }
  return "http://172.16.5.16:8000";
}

// GET /search/status — checks FastAPI health via Next.js server proxy
// Uses only the server-side proxy route (works through SSH tunnels / VS Code port forwarding)
export async function getSearchBackendStatus(): Promise<{
  online: boolean;
  swagger_ui: string;
  backend_url: string;
}> {
  try {
    const res = await fetch("/api/search/status", {
      cache: "no-store",
      headers: { "Cache-Control": "no-cache", Pragma: "no-cache" },
      signal: AbortSignal.timeout(60_000),
    });
    if (res.ok) {
      const data = await res.json();
      if (data.online) {
        return {
          online: true,
          backend_url: data.backend_url || "",
          swagger_ui: data.swagger_ui || "",
        };
      }
    }
  } catch (err) {
    console.warn("[getSearchBackendStatus] proxy check failed:", err);
  }

  return {
    online: false,
    backend_url: "",
    swagger_ui: "",
  };
}

// POST /api/search — searches precedents via Next.js server proxy
export async function search(params: {
  workspace_id?: string;
  mode?: "keyword" | "semantic" | "similar" | "issue";
  query: string;
  top_k?: number;
  court?: string;
  court_filter?: string | null;
  year_from?: number;
  year_to?: number;
  include_full_text?: boolean;
}): Promise<SemanticSearchResponse> {
  const courtVal = params.court_filter || (params.court && params.court !== "ALL" ? params.court : null);
  const payload = {
    query: params.query,
    top_k: params.top_k || 5,
    court_filter: courtVal || null,
    year_from: params.year_from || undefined,
    year_to: params.year_to || undefined,
  };

  const res = await fetch("/api/search", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
    signal: AbortSignal.timeout(300_000),
  });

  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    throw new Error(errorData.error || errorData.detail || `Backend error: HTTP ${res.status}`);
  }

  return res.json();
}

// GET /api/judgments/{judgment_id} — fetches full precedent judgment details
export async function getJudgmentDetails(judgmentId: string): Promise<{
  id: string;
  neutral_cite: string | null;
  court: string | null;
  decided_on: string | null;
  style_of_cause: string | null;
  source_url: string | null;
  full_text: string | null;
}> {
  const res = await fetch(`/api/judgments/${judgmentId}`, {
    headers: { "Cache-Control": "no-cache" },
    signal: AbortSignal.timeout(60_000),
  });

  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    throw new Error(errorData.error || `Judgment lookup failed: HTTP ${res.status}`);
  }

  return res.json();
}




export interface ResearchMemoRequest {
  case_text?: string;
  additional_authorities?: string;
  to_user?: string; // Default: "Supervising Lawyer"
  from_user?: string; // Default: "Research Assistant"
  file_number?: string;
  specific_questions?: string;
  tone?: "objective" | "advocacy";
  numbered_paragraphs?: boolean;
}

export interface ResearchMemoData {
  memo_markdown: string; // Ready to render in Markdown viewer
  to_user: string;
  from_user: string;
  date: string;
  subject: string;
}

export interface ResearchMemoResponse {
  success: boolean;
  data: ResearchMemoData | null;
  error: string | null;
}

/**
 * POST /api/documents/generate-memo
 * Generates an Ontario Research Memo directly from an uploaded case file (.pdf, .docx, .png, .jpg).
 */
export async function generateResearchMemoFromFile(
  file: File,
  options: Partial<ResearchMemoRequest> = {}
): Promise<ResearchMemoResponse> {
  const formData = new FormData();
  formData.append("file", file);
  if (options.to_user) formData.append("to_user", options.to_user);
  if (options.from_user) formData.append("from_user", options.from_user);
  if (options.file_number) formData.append("file_number", options.file_number);
  if (options.specific_questions) formData.append("specific_questions", options.specific_questions);
  if (options.tone) formData.append("tone", options.tone);
  if (options.numbered_paragraphs !== undefined) {
    formData.append("numbered_paragraphs", String(options.numbered_paragraphs));
  }

  const res = await fetch("http://127.0.0.1:8000/api/documents/generate-memo", {
    method: "POST",
    body: formData,
  });

  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    throw new Error(errorData.error || errorData.detail || `Failed to generate memo: HTTP ${res.status}`);
  }

  return res.json();
}

export interface RAGAskRequest {
  query: string;
  top_k?: number; // Default 5
  court_filter?: string | null;
  year_from?: number | null;
  year_to?: number | null;
}

export interface RAGCitation {
  case_name: string;
  neutral_cite: string;
  paragraph_range: string;
  relevance_score: number;
}

export interface RAGData {
  answer: string;
  citations: RAGCitation[];
  sources_used: number;
}

export interface RAGAskResponse {
  success: boolean;
  data: RAGData | null;
  error: string | null;
}

/**
 * POST /api/rag/ask
 * Performs legal precedent retrieval + grounded LLM answer generation with verified case citations.
 */
export async function askRagQuestion(params: RAGAskRequest): Promise<RAGAskResponse> {
  const res = await fetch("/api/rag/ask", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      query: params.query,
      top_k: params.top_k ?? 5,
      court_filter: params.court_filter ?? null,
      year_from: params.year_from ?? null,
      year_to: params.year_to ?? null,
    }),
  });

  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    throw new Error(errorData.error || errorData.detail || `RAG Question Answering failed: HTTP ${res.status}`);
  }

  return res.json();
}

export interface DemandLetterRequest {
  case_text?: string;
  sender_client?: string; // Default: "Our Client"
  recipient_name?: string; // Default: "Opposing Party"
  nature_of_claim?: string;
  remedy_sought?: string;
  deadline_days?: number; // Default: 14
  forum?: string; // e.g. "Superior Court of Justice"
  tone?: "firm and formal" | "conciliatory";
  delivery_method?: string; // Default: "REGISTERED MAIL / EMAIL"
}

export interface DemandLetterData {
  letter_markdown: string; // Ready to render in Markdown viewer
  sender_client: string;
  recipient_name: string;
  date: string;
  subject: string;
}

export interface DemandLetterResponse {
  success: boolean;
  data: DemandLetterData | null;
  error: string | null;
}

/**
 * POST /api/documents/generate-demand-letter
 * Generates an Ontario Legal Demand Letter directly from an uploaded case file (.pdf, .docx, .png, .jpg).
 */
export async function generateDemandLetterFromFile(
  file: File,
  options: Partial<DemandLetterRequest> = {}
): Promise<DemandLetterResponse> {
  const formData = new FormData();
  formData.append("file", file);
  if (options.sender_client) formData.append("sender_client", options.sender_client);
  if (options.recipient_name) formData.append("recipient_name", options.recipient_name);
  if (options.nature_of_claim) formData.append("nature_of_claim", options.nature_of_claim);
  if (options.remedy_sought) formData.append("remedy_sought", options.remedy_sought);
  if (options.deadline_days !== undefined) formData.append("deadline_days", String(options.deadline_days));
  if (options.forum) formData.append("forum", options.forum);
  if (options.tone) formData.append("tone", options.tone);
  if (options.delivery_method) formData.append("delivery_method", options.delivery_method);

  const res = await fetch("http://127.0.0.1:8000/api/documents/generate-demand-letter", {
    method: "POST",
    body: formData,
  });

  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    throw new Error(errorData.error || errorData.detail || `Failed to generate demand letter: HTTP ${res.status}`);
  }

  return res.json();
}

export interface DocRequestListRequest {
  case_text?: string;
  directed_to?: string; // Default: "Opposing Party"
  from_user?: string; // Default: "Counsel"
  file_number?: string;
  specific_issues?: string;
  proceeding_stage?: string; // Default: "discovery"
  deadline_days?: number; // Default: 30
}

export interface DocRequestListData {
  list_markdown: string; // Ready to render in Markdown viewer
  directed_to: string;
  from_user: string;
  date: string;
  subject: string;
}

export interface DocRequestListResponse {
  success: boolean;
  data: DocRequestListData | null;
  error: string | null;
}

/**
 * POST /api/documents/generate-doc-request
 * Generates an Ontario Document Request List directly from an uploaded case file (.pdf, .docx, .png, .jpg).
 */
export async function generateDocRequestListFromFile(
  file: File,
  options: Partial<DocRequestListRequest> = {}
): Promise<DocRequestListResponse> {
  const formData = new FormData();
  formData.append("file", file);
  if (options.directed_to) formData.append("directed_to", options.directed_to);
  if (options.from_user) formData.append("from_user", options.from_user);
  if (options.file_number) formData.append("file_number", options.file_number);
  if (options.specific_issues) formData.append("specific_issues", options.specific_issues);
  if (options.proceeding_stage) formData.append("proceeding_stage", options.proceeding_stage);
  if (options.deadline_days !== undefined) formData.append("deadline_days", String(options.deadline_days));

  const res = await fetch("http://127.0.0.1:8000/api/documents/generate-doc-request", {
    method: "POST",
    body: formData,
  });

  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    throw new Error(errorData.error || errorData.detail || `Failed to generate document request list: HTTP ${res.status}`);
  }

  return res.json();
}

// POST /workspaces/{id}/generate
export async function generateDocument(params: {
  workspace_id: string;
  doc_type: string;
  document_id?: string;
  auto_insert_authorities?: boolean;
  memo_options?: Partial<ResearchMemoRequest>;
  demand_options?: Partial<DemandLetterRequest>;
  doc_request_options?: Partial<DocRequestListRequest>;
}): Promise<{ job_id: string; content: string; document?: WorkspaceDocument }> {
  const res = await fetch(`/api/workspaces/${params.workspace_id}/generate`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      doc_type: params.doc_type,
      document_id: params.document_id,
      auto_insert_authorities: params.auto_insert_authorities,
      memo_options: params.memo_options,
      demand_options: params.demand_options,
      doc_request_options: params.doc_request_options,
    }),
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    throw new Error(errorData.error || `Failed to generate ${params.doc_type}`);
  }
  return res.json();
}

// GET /generations/{id}
export async function getGeneration(_jobId: string) {
  return delay({
    status: "complete",
    text: "Mock generated document body...",
    citations: [SUPPORTING_AUTHORITY],
  });
}

// POST /workspaces/{id}/counter-questions
export async function generateCounterQuestions(_workspaceId: string) {
  return delay({
    questions: [
      { question: "Why was no notice period offered before termination?", severity: "high" },
      { question: "What mitigation efforts has the plaintiff made?", severity: "medium" },
    ],
  });
}

// GET /judgments/{id}/treatment
export async function getTreatment(_judgmentId: string) {
  return delay(SUPPORTING_AUTHORITY);
}

// POST /api/workspaces/{id}/generate-claim
export async function generateStatementOfClaim(
  workspaceId: string
): Promise<{ document: WorkspaceDocument; content: string }> {
  const res = await fetch(`/api/workspaces/${workspaceId}/generate-claim`, {
    method: "POST",
  });
  const data = await res.json();
  if (!res.ok || data.error) {
    throw new Error(data.error || "Failed to generate Statement of Claim");
  }
  return data;
}

// GET /api/workspaces/{id}/document-checklist
export async function getDocumentChecklist(workspaceId: string) {
  const res = await fetch(`/api/workspaces/${workspaceId}/document-checklist`);
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Failed to fetch document checklist");
  return data;
}

// GET /api/workspaces/{id}/document-gaps
export async function getDocumentGaps(workspaceId: string, caseType?: string) {
  const url = caseType
    ? `/api/workspaces/${workspaceId}/document-gaps?case_type=${encodeURIComponent(caseType)}`
    : `/api/workspaces/${workspaceId}/document-gaps`;
  const res = await fetch(url);
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Failed to detect document gaps");
  return data;
}

// POST /api/workspaces/{id}/generate-request-letter
export async function generateDocumentRequestLetter(params: {
  workspaceId: string;
  recipientName: string;
  deadlineDate: string;
  approvedItems: string[];
  suggestedItems?: { item: string; source: string; reason?: string }[];
}): Promise<{ content: string }> {
  const res = await fetch(`/api/workspaces/${params.workspaceId}/generate-request-letter`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      recipient_name: params.recipientName,
      deadline_date: params.deadlineDate,
      approved_items: params.approvedItems,
      suggested_items: params.suggestedItems,
    }),
  });
  const data = await res.json();
  if (!res.ok || data.error) {
    throw new Error(data.error || "Failed to generate request letter");
  }
  return data;
}

// PATCH /api/workspaces/{id}/document-request
export async function updateDocumentRequestDraft(params: {
  workspaceId: string;
  caseType: string;
  recipientName: string;
  deadlineDate: string;
  suggestedItems: { item: string; source: string; reason?: string }[];
  approvedItems: string[];
  status?: "draft" | "approved" | "sent";
}) {
  const res = await fetch(`/api/workspaces/${params.workspaceId}/document-request`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      case_type: params.caseType,
      recipient_name: params.recipientName,
      deadline_date: params.deadlineDate,
      suggested_items: params.suggestedItems,
      lawyer_approved_items: params.approvedItems,
      status: params.status || "approved",
    }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Failed to save draft");
  return data;
}

export interface FlaggedClause {
  clause_id: number;
  heading: string;
  page_number: number;
  clause_text: string;
  reference_matched_title: string;
  risk_level: "high" | "medium" | "low";
  obligation_or_right: string;
  comparison_to_standard: string;
  risks_and_ambiguities: string;
  missing_elements: string;
  recommendation: string;
}

export interface ContractAnalysisResult {
  filename: string;
  total_clauses_analyzed: number;
  risk_summary: {
    high: number;
    medium: number;
    low: number;
  };
  flagged_clauses: FlaggedClause[];
}

// GET /api/search/stats — returns live Vector Database (Qdrant) and PostgreSQL statistics
export async function getSearchStats() {
  const directBase = getFastApiDirectBase();
  const res = await fetch(`${directBase}/api/search/stats`);
  if (!res.ok) {
    throw new Error(`Failed to fetch search stats: HTTP ${res.status}`);
  }
  return res.json();
}

// POST /api/extract-text — uploads PDF/Image for OCR and text extraction
export async function submitExtractTextJob(file: File, forceReprocess = false): Promise<{ job_id: string; status: string; cached: boolean }> {
  const directBase = getFastApiDirectBase();
  const formData = new FormData();
  formData.append("file", file);

  const res = await fetch(`${directBase}/api/extract-text?force_reprocess=${forceReprocess}`, {
    method: "POST",
    body: formData,
  });

  const data = await res.json();
  if (!res.ok || data.error || data.detail) {
    throw new Error(data.error || data.detail || "Failed to submit document extraction job");
  }
  return data;
}

// GET /api/extract-text/{job_id} — polls status of an active extraction job
export async function getExtractTextJobStatus(jobId: string) {
  const directBase = getFastApiDirectBase();
  const res = await fetch(`${directBase}/api/extract-text/${jobId}`);
  if (!res.ok) {
    throw new Error(`Failed to fetch job status: HTTP ${res.status}`);
  }
  return res.json();
}

// DELETE /api/extract-text/{job_id} — cancels an extraction job
export async function cancelExtractTextJob(jobId: string) {
  const directBase = getFastApiDirectBase();
  const res = await fetch(`${directBase}/api/extract-text/${jobId}`, {
    method: "DELETE",
  });
  if (!res.ok) {
    throw new Error(`Failed to cancel job: HTTP ${res.status}`);
  }
  return res.json();
}

// ─────────────────────────────────────────────────────────────────────────────
// CONVERSATIONAL RAG CHAT ENDPOINTS
// ─────────────────────────────────────────────────────────────────────────────

export interface ChatConversation {
  id: string;
  title: string;
  case_id?: string | null;
  created_at: string;
  updated_at: string;
  messages?: Array<{
    id: string;
    role: "user" | "assistant";
    content: string;
    citations?: any[];
    created_at: string;
  }>;
}

// POST /api/chat/conversations
export async function createChatConversation(params?: { case_id?: string; title?: string; user_id?: string }): Promise<ChatConversation> {
  const res = await fetch("/api/chat/conversations", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(params || {}),
  });
  const data = await res.json();
  if (!res.ok || data.error) {
    throw new Error(data.error || "Failed to create chat conversation");
  }
  const payload = data.data || data;
  return {
    id: payload.id || payload.conversation_id,
    title: payload.title || "Employment Case Chat",
    case_id: payload.case_id,
    created_at: payload.created_at || new Date().toISOString(),
    updated_at: payload.updated_at || new Date().toISOString(),
    messages: payload.messages || [],
  };
}

// GET /api/chat/conversations
export async function listChatConversations(params?: { limit?: number; offset?: number; case_id?: string; user_id?: string }): Promise<ChatConversation[]> {
  const query = new URLSearchParams();
  if (params?.limit) query.set("limit", String(params.limit));
  if (params?.offset) query.set("offset", String(params.offset));
  if (params?.case_id) query.set("case_id", params.case_id);
  if (params?.user_id) query.set("user_id", params.user_id);

  const res = await fetch(`/api/chat/conversations?${query.toString()}`);
  const data = await res.json();
  if (!res.ok || data.error) {
    return [];
  }
  const payload = data.data || data;
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload.items)) return payload.items;
  if (Array.isArray(payload.conversations)) return payload.conversations;
  if (Array.isArray(data.items)) return data.items;
  if (Array.isArray(data.conversations)) return data.conversations;
  return [];
}

// GET /api/chat/conversations/{id}
export async function getChatConversationHistory(conversationId: string): Promise<ChatConversation | null> {
  const res = await fetch(`/api/chat/conversations/${conversationId}`);
  const data = await res.json();
  if (!res.ok || data.error) {
    return null;
  }
  const payload = data.data || data;
  return {
    id: payload.id || conversationId,
    title: payload.title || "Chat History",
    case_id: payload.case_id,
    created_at: payload.created_at || new Date().toISOString(),
    updated_at: payload.updated_at || new Date().toISOString(),
    messages: payload.messages || payload.history || [],
  };
}

// POST /api/chat/conversations/{id}/messages
export async function sendChatMessage(
  conversationId: string,
  content: string,
  history?: Array<{ role: string; content: string }>
): Promise<{ id: string; role: "assistant"; content: string; citations?: any[] }> {
  const res = await fetch(`/api/chat/conversations/${conversationId}/messages`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ content, history }),
  });
  const data = await res.json();
  if (!res.ok || data.error) {
    throw new Error(data.error || "Failed to send chat message");
  }
  const payload = data.data || data;
  return {
    id: payload.id || `msg-${Date.now()}`,
    role: "assistant",
    content: payload.content || payload.answer || payload.text || "",
    citations: payload.citations || [],
  };
}

// POST /api/chat/conversations/{id}/upload
export async function uploadFileToChatConversation(
  conversationId: string,
  file: File
): Promise<{ success: boolean; filename: string }> {
  const formData = new FormData();
  formData.append("file", file);

  const res = await fetch(`/api/chat/conversations/${conversationId}/upload`, {
    method: "POST",
    body: formData,
  });
  const data = await res.json();
  if (!res.ok || data.error) {
    throw new Error(data.error || "Failed to upload file to conversation");
  }
  return data;
}

// DELETE /api/chat/conversations/{id}
export async function deleteChatConversation(conversationId: string): Promise<void> {
  const res = await fetch(`/api/chat/conversations/${conversationId}`, {
    method: "DELETE",
  });
  if (res.status === 404) {
    // Session is already deleted from backend database
    return;
  }
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data.error) {
    throw new Error(data.error || "Failed to delete conversation");
  }
}





// GET /api/search/summaries — fetches background-generated case summaries
export async function fetchPendingSummaries(
  judgmentIds: string[]
): Promise<Record<string, { status: string; summary?: string | null }>> {
  if (!judgmentIds || judgmentIds.length === 0) return {};
  try {
    const res = await fetch(`/api/search/summaries?ids=${encodeURIComponent(judgmentIds.join(","))}`);
    if (!res.ok) return {};
    return await res.json();
  } catch (err) {
    console.warn("Failed to fetch pending summaries:", err);
    return {};
  }
}



export interface SearchResultItem {
  judgment_id?: string;
  chunk_id?: string;
  score?: number;
  para_start?: number;
  para_end?: number;
  text?: string;
  chunk_text?: string;
  case_name?: string;
  neutral_citation?: string;
  court?: string;
  year?: number;
}

export interface SearchResponsePayload {
  query?: string;
  total_found?: number;
  results?: SearchResultItem[];
}

export async function performSemanticSearch(params: {
  query: string;
  top_k?: number;
  court_filter?: string;
  year_from?: number;
  year_to?: number;
}): Promise<SearchResponsePayload> {
  const res = await fetch(`/api/search`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(params),
  });

  if (!res.ok) {
    const errData = await res.json().catch(() => ({}));
    throw new Error(errData.detail || `Search failed with status ${res.status}`);
  }

  return res.json();
}

// ── SSE Streaming Chat ─────────────────────────────────────────────────────

export interface StreamChatCallbacks {
  onRetrievalDone?: () => void;
  onToken: (token: string) => void;
  onDone: (result: { id: string; content: string; citations: any[] }) => void;
  onError: (error: Error) => void;
}

/**
 * POST /api/chat/conversations/{id}/messages/stream
 *
 * Consumes the SSE stream from the backend and dispatches callbacks
 * for each event type: retrieval_done, token, done, error.
 *
 * Returns an AbortController so the caller can cancel the stream.
 */
export function streamChatMessage(
  conversationId: string,
  content: string,
  history: Array<{ role: string; content: string }> | undefined,
  callbacks: StreamChatCallbacks
): AbortController {
  const controller = new AbortController();

  (async () => {
    try {
      const res = await fetch(
        `/api/chat/conversations/${conversationId}/messages/stream`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ content, history }),
          signal: controller.signal,
        }
      );

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || `Stream failed: HTTP ${res.status}`);
      }

      if (!res.body) {
        throw new Error("No stream body in response");
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        // Parse SSE frames: "event: <type>\ndata: <json>\n\n"
        const frames = buffer.split("\n\n");
        // Keep the last incomplete frame in the buffer
        buffer = frames.pop() || "";

        for (const frame of frames) {
          if (!frame.trim()) continue;

          let eventType = "message";
          let eventData = "";

          for (const line of frame.split("\n")) {
            if (line.startsWith("event: ")) {
              eventType = line.slice(7).trim();
            } else if (line.startsWith("data: ")) {
              eventData = line.slice(6);
            }
          }

          switch (eventType) {
            case "retrieval_done":
              callbacks.onRetrievalDone?.();
              break;

            case "token": {
              try {
                const token = JSON.parse(eventData) as string;
                callbacks.onToken(token);
              } catch {
                // Raw text fallback
                if (eventData) callbacks.onToken(eventData);
              }
              break;
            }

            case "done": {
              try {
                const payload = JSON.parse(eventData);
                callbacks.onDone({
                  id: payload.id || `msg-${Date.now()}`,
                  content: payload.content || "",
                  citations: payload.citations || [],
                });
              } catch {
                callbacks.onDone({
                  id: `msg-${Date.now()}`,
                  content: eventData,
                  citations: [],
                });
              }
              break;
            }

            case "error": {
              try {
                const errPayload = JSON.parse(eventData);
                callbacks.onError(new Error(errPayload.message || "Stream error"));
              } catch {
                callbacks.onError(new Error(eventData || "Unknown stream error"));
              }
              break;
            }
          }
        }
      }
    } catch (err: unknown) {
      if (err instanceof DOMException && err.name === "AbortError") {
        return; // Intentional abort, not an error
      }
      callbacks.onError(
        err instanceof Error ? err : new Error("Stream connection failed")
      );
    }
  })();

  return controller;
}

// ── SSE Streaming Document Generation ──────────────────────────────────────

export interface StreamDocGenCallbacks {
  onStage?: (stage: { stage: string; label: string; detail: string; icon?: string }) => void;
  onToken: (token: string) => void;
  onDone: (result: { content: string; docId?: string; filename?: string }) => void;
  onError: (error: Error) => void;
}

/**
 * POST /api/workspaces/{id}/generate/stream
 *
 * Real-time SSE streaming for legal document generation (Statement of Claim,
 * Research Memo, Demand Letter, Document Request List, etc.)
 */
export function streamDocumentGeneration(
  workspaceId: string,
  params: {
    doc_type: string;
    document_id?: string;
    auto_insert_authorities?: boolean;
    memo_options?: any;
    demand_options?: any;
    doc_request_options?: any;
  },
  callbacks: StreamDocGenCallbacks
): AbortController {
  const controller = new AbortController();

  (async () => {
    try {
      const res = await fetch(`/api/workspaces/${workspaceId}/generate/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(params),
        signal: controller.signal,
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || `Generation failed: HTTP ${res.status}`);
      }

      if (!res.body) {
        throw new Error("No stream body in response");
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const frames = buffer.split("\n\n");
        buffer = frames.pop() || "";

        for (const frame of frames) {
          if (!frame.trim()) continue;

          let eventType = "message";
          let eventData = "";

          for (const line of frame.split("\n")) {
            if (line.startsWith("event: ")) {
              eventType = line.slice(7).trim();
            } else if (line.startsWith("data: ")) {
              eventData = line.slice(6);
            }
          }

          switch (eventType) {
            case "stage": {
              try {
                const stageObj = JSON.parse(eventData);
                callbacks.onStage?.(stageObj);
              } catch {}
              break;
            }

            case "token": {
              try {
                const token = JSON.parse(eventData) as string;
                callbacks.onToken(token);
              } catch {
                if (eventData) callbacks.onToken(eventData);
              }
              break;
            }

            case "done": {
              try {
                const payload = JSON.parse(eventData);
                callbacks.onDone({
                  content: payload.content || "",
                  docId: payload.docId,
                  filename: payload.filename,
                });
              } catch {
                callbacks.onDone({ content: eventData });
              }
              break;
            }

            case "error": {
              try {
                const errPayload = JSON.parse(eventData);
                callbacks.onError(new Error(errPayload.message || "Generation error"));
              } catch {
                callbacks.onError(new Error(eventData || "Unknown generation error"));
              }
              break;
            }
          }
        }
      }
    } catch (err: unknown) {
      if (err instanceof DOMException && err.name === "AbortError") {
        return;
      }
      callbacks.onError(
        err instanceof Error ? err : new Error("Document stream failed")
      );
    }
  })();

  return controller;
}

/**
 * POST /api/workspaces/{id}/generate-request-letter/stream
 *
 * Real-time SSE streaming for grounded Ontario Document Request Letters.
 */
export function streamDocumentRequestLetter(
  workspaceId: string,
  params: {
    recipientName?: string;
    deadlineDate?: string;
    approvedItems: string[];
    suggestedItems?: any[];
  },
  callbacks: StreamDocGenCallbacks
): AbortController {
  const controller = new AbortController();

  (async () => {
    try {
      const res = await fetch(`/api/workspaces/${workspaceId}/generate-request-letter/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(params),
        signal: controller.signal,
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || `Generation failed: HTTP ${res.status}`);
      }

      if (!res.body) {
        throw new Error("No stream body in response");
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const frames = buffer.split("\n\n");
        buffer = frames.pop() || "";

        for (const frame of frames) {
          if (!frame.trim()) continue;

          let eventType = "message";
          let eventData = "";

          for (const line of frame.split("\n")) {
            if (line.startsWith("event: ")) {
              eventType = line.slice(7).trim();
            } else if (line.startsWith("data: ")) {
              eventData = line.slice(6);
            }
          }

          switch (eventType) {
            case "stage": {
              try {
                const stageObj = JSON.parse(eventData);
                callbacks.onStage?.(stageObj);
              } catch {}
              break;
            }

            case "token": {
              try {
                const token = JSON.parse(eventData) as string;
                callbacks.onToken(token);
              } catch {
                if (eventData) callbacks.onToken(eventData);
              }
              break;
            }

            case "done": {
              try {
                const payload = JSON.parse(eventData);
                callbacks.onDone({
                  content: payload.content || "",
                  docId: payload.docId,
                  filename: payload.filename,
                });
              } catch {
                callbacks.onDone({ content: eventData });
              }
              break;
            }

            case "error": {
              try {
                const errPayload = JSON.parse(eventData);
                callbacks.onError(new Error(errPayload.message || "Generation error"));
              } catch {
                callbacks.onError(new Error(eventData || "Unknown generation error"));
              }
              break;
            }
          }
        }
      }
    } catch (err: unknown) {
      if (err instanceof DOMException && err.name === "AbortError") {
        return;
      }
      callbacks.onError(
        err instanceof Error ? err : new Error("Document stream failed")
      );
    }
  })();

  return controller;
}
